import { useReportRange } from '../../shared/ReportLead';
import { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Typography, LinearProgress, Card, CardContent, Alert, Button } from '@mui/material';
import ResponsiveTabs from '../../../system/responsive-tabs';
import { getAnalyticsReportTabsSx } from '../../shared/report-design';
import { resolveShowCost } from '../../../system/report-visibility';
import { formatUsd } from '../../../system/report-formatters';


const TABS = [
  { label: "Images", key: 0 },
  { label: "Videos", key: 4 },
  { label: "Headlines", key: 1 },
  { label: "AD Copy", key: 2 },
  { label: "Call to Action", key: 3 },
];

function CreativeDetailTabs({ rowData, appVars, reportProfile, conversions = [] }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [detailData, setDetailData] = useState([[], [], [], [], []]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const range = useReportRange();
  const nodeId = rowData.ADSET_ID || rowData.ID;
  const conversionKey = JSON.stringify(conversions);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const showCost = resolveShowCost(reportProfile);

  const normalizeTextItems = (source, keys) => {
    const output = [];
    const push = (val, raw) => {
      if (val == null) return;
      if (Array.isArray(val)) {
        val.forEach((item) => push(item, raw));
        return;
      }
      if (typeof val === 'object') {
        const key = keys.find((k) => val[k] != null);
        if (key) {
          push(val[key], val);
          return;
        }
        if (val.text != null) return push(val.text, val);
        if (val.label != null) return push(val.label, val);
        if (val.value != null) return push(val.value, val);
        if (val.name != null) return push(val.name, val);
        return;
      }
      const text = String(val).trim();
      if (text) output.push({ text, raw });
    };

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const key = keys.find((k) => source[k] != null);
      if (key) {
        push(source[key], source);
        return output;
      }
    }

    push(source, source);
    return output;
  };

  const normalizeImageItems = (source) => {
    const output = [];
    const push = (val, raw) => {
      if (val == null) return;
      if (Array.isArray(val)) {
        val.forEach((item) => push(item, raw));
        return;
      }
      if (typeof val === 'object') {
        const url =
          val.IMAGE_ASSET_URL ||
          val.IMAGE_URL ||
          val.URL ||
          val.url ||
          val.image_url ||
          val.image;
        const name = val.NAME || val.name || val.filename || val.FILE_NAME || val.TITLE || val.title;
        if (url) {
          output.push({ url, name: name || url, raw: val });
          return;
        }
        const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image'].find((k) => val[k] != null);
        if (key) {
          push(val[key], val);
        }
        return;
      }
      const text = String(val).trim();
      if (text) output.push({ url: text, name: text, raw });
    };

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image'].find((k) => source[k] != null);
      if (key) {
        push(source[key], source);
        return output;
      }
    }

    push(source, source);
    return output;
  };

  const hasAssets = detailData.some(part => Array.isArray(part) ? part.length > 0 : Boolean(part));
  const imageItems = useMemo(() => normalizeImageItems(detailData[0]), [detailData]);
  const videoItems = useMemo(() => normalizeImageItems((Array.isArray(detailData[4]) ? detailData[4] : []).filter(row => row.VIDEO_THUMB_URL).map(row => ({ ...row, IMAGE_URL: row.VIDEO_THUMB_URL, NAME: `Video ${row.VIDEO || ''}` }))), [detailData]);
  const headlineItems = useMemo(() => normalizeTextItems(detailData[1], ['TITLE', 'HEADLINE', 'TEXT', 'NAME']), [detailData]);
  const adCopyItems = useMemo(() => normalizeTextItems(detailData[2], ['BODY_COPY', 'BODY', 'TEXT', 'COPY', 'MESSAGE', 'DESCRIPTION']), [detailData]);
  const ctaItems = useMemo(() => normalizeTextItems(detailData[3], ['CTA', 'CALL_TO_ACTION', 'ACTION', 'TEXT', 'TITLE', 'NAME']), [detailData]);

  const formatNumber = (value) => value == null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
  const formatMoney = (value) => value == null ? '—' : formatUsd(Number(value));
  const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

  const pickMetric = (row, keys) => {
    if (!row || typeof row !== 'object') return null;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) return Number(row[key]);
    }
    const lowerKeys = keys.map((key) => String(key).toLowerCase());
    const rowKey = Object.keys(row).find((k) => lowerKeys.includes(String(k).toLowerCase()));
    if (rowKey) return Number(row[rowKey]);
    return null;
  };

  const getStats = (row) => {
    if (!row || typeof row !== 'object') return null;
    const impressions = pickMetric(row, ['IMPRESSIONS', 'impressions']);
    const clicks = pickMetric(row, ['CLICKS', 'clicks']);
    const conversions = pickMetric(row, ['CONVERSIONS', 'conversions']);
    const cost = pickMetric(row, ['COST', 'cost', 'SPEND', 'spend']);
    const revenue = pickMetric(row, ['REVENUE', 'revenue']);
    const ctrRaw = pickMetric(row, ['CTR', 'ctr']);
    const convRateRaw = pickMetric(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']);
    const ctr = ctrRaw ?? (impressions && impressions > 0 ? (Number(clicks || 0) / impressions) * 100 : null);
    const convRate = convRateRaw ?? (clicks && clicks > 0 ? (Number(conversions || 0) / clicks) * 100 : null);
    const roas = cost && cost > 0 && revenue != null ? revenue / cost : null;
    const hasAny = [impressions, clicks, conversions, cost, revenue, ctr, convRate].some((val) => val !== null && val !== undefined);
    return { impressions, clicks, conversions, cost, revenue, ctr, convRate, roas, hasAny };
  };

  const summaryStats = getStats(rowData);
  const summaryItems = useMemo(() => {
    if (!summaryStats?.hasAny) return [];
    const items = [
      { label: 'Impressions', value: summaryStats.impressions, format: formatNumber },
      { label: 'Clicks', value: summaryStats.clicks, format: formatNumber },
      { label: 'Conversions', value: summaryStats.conversions, format: formatNumber },
      { label: 'Revenue', value: summaryStats.revenue, format: formatMoney },
      { label: 'CTR', value: summaryStats.ctr, format: formatPercent },
      { label: 'Conversion rate', value: summaryStats.convRate, format: formatPercent },
    ];
    if (showCost) {
      items.push(
        { label: 'Spend', value: summaryStats.cost, format: formatMoney },
        { label: 'ROAS', value: summaryStats.roas, format: (val) => (val != null ? `${Number(val).toFixed(2)}x` : '0.00x') }
      );
    }
    return items.filter((item) => item.value !== null && item.value !== undefined);
  }, [showCost, summaryStats]);



  useEffect(() => {
    let active = true;
    setDetailData([[], [], [], [], []]);
    setTabIndex(0);
    setError('');
    if (!nodeId || !appVars.api_url) { setLoading(false); return undefined; }
    setLoading(true);
    const load = async () => {
      try {
        const response = await fetch(`${appVars.api_url}/meta/creatives/adgroup`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cid: sessionStorage.getItem('rid'), adgroup: nodeId, sdate: range.sDate, edate: range.eDate, ...(JSON.parse(conversionKey).length ? { conversions: JSON.parse(conversionKey) } : {}) })
        });
        const payload = await response.json();
        if (!response.ok || !Array.isArray(payload)) throw new Error('Creative detail could not load for this audience.');
        if (active) {
          setDetailData([payload[0] || [], payload[1] || [], payload[2] || [], payload[3] || [], payload[4] || []]);
          setTabIndex([0, 4, 1, 2, 3].find(index => Array.isArray(payload[index]) && payload[index].length) ?? 0);
        }
      } catch (err) { if (active) setError(err.message); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [nodeId, appVars.api_url, range.sDate, range.eDate, conversionKey, revision]);

  const assetMetrics = raw => {
    const stats = getStats(raw);
    return stats?.hasAny ? <Typography variant="caption" color="text.secondary">{formatNumber(stats.clicks)} clicks · {formatNumber(stats.conversions)} conversions · {formatMoney(stats.revenue)} revenue</Typography> : null;
  };

  return (
    <Box sx={{ p: 1, bgcolor: "background.default", minWidth: 0 }}>
      {summaryItems.length ? (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {rowData.CREATIVE || rowData.ADSET || 'Audience'}
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
              {summaryItems.map((item) => (
                <Box key={item.label} sx={{ display: 'grid', gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {item.format(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Audience performance is reported by Meta ad set. The creative assets below belong to this set for the selected dates and conversions. Asset results can overlap; do not add them together.</Typography>
      {!nodeId && <Alert severity="info">This audience has no ad-set identifier, so creative details are unavailable.</Alert>}
      {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setRevision(value => value + 1)}>Retry</Button>}>{error}</Alert>}
      {hasAssets && <ResponsiveTabs aria-label="Creative detail pages" sx={getAnalyticsReportTabsSx()} value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
        {TABS.map(tab => <Tab key={tab.key} value={tab.key} label={tab.label} />)}
      </ResponsiveTabs>}
      <Box sx={{ pt: 2 }}>
        {loading ? <LinearProgress /> : hasAssets ? (
          <>
            {tabIndex === 0 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                {imageItems.length ? imageItems.map((item, index) => (
                  <Card key={`${item.url}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 120,
                          borderRadius: 1,
                          border: '1px solid rgba(0,0,0,0.08)',
                          bgcolor: 'background.paper',
                          p: 0.5
                        }}
                      >
                        <img
                          style={{ maxWidth: '100%', maxHeight: 140, cursor: 'pointer' }}
                          alt={item.name}
                          src={item.url}
                          onClick={() => {
                            setLightboxImage(item.url);
                            setLightboxOpen(true);
                          }}
                          onError={e => {
                            e.target.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap title={item.name}>
                        {item.name}
                      </Typography>
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No images available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 4 && <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
              {videoItems.length ? videoItems.map(item => <Card key={item.raw.VIDEO || item.url} variant="outlined"><CardContent sx={{ display: 'grid', gap: 1 }}>
                <Box component="img" src={item.url} alt={item.name} loading="lazy" sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', bgcolor: 'background.default', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Video thumbnail · Results for this asset</Typography>
                {assetMetrics(item.raw)}
              </CardContent></Card>) : <Typography variant="body2" color="text.secondary">No video thumbnails were returned for this set.</Typography>}
            </Box>}
            {tabIndex === 1 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {headlineItems.length ? headlineItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Headline {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                      {assetMetrics(text.raw)}
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No headlines available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 2 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {adCopyItems.length ? adCopyItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Ad Copy {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                      {assetMetrics(text.raw)}
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No ad copy available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 3 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {ctaItems.length ? ctaItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">CTA {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                      {assetMetrics(text.raw)}
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No calls to action available.</Typography>
                )}
              </Box>
            )}
          </>
        ) : !error && nodeId ? <Alert severity="info">Meta did not return creative assets for this audience and date range. The performance above uses the reported ad-set totals.</Alert> : null}
      </Box>
      {lightboxOpen && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img
            src={lightboxImage}
            alt="Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              boxShadow: '0 0 20px #000',
              borderRadius: 8,
            }}
          />
        </Box>
      )}
    </Box>








  );
}

export default CreativeDetailTabs;
